/**
 * useSavedInvites Hook
 *
 * Manages saved invites for the Email Template Generator.
 * Stores invites in PostgreSQL via the userSettingsService.
 *
 * Features:
 * - Save/Load invite configurations with all selections
 * - Track export history (HTML, EML, Mail Merge)
 * - Mark invites as completed
 * - Track delivered recipients
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { getSetting, setSetting } from '@shared/services/userSettingsService';
import type {
  SavedInvite,
  InviteRecipient,
  ExportHistoryEntry,
} from '../types/emailTemplates';

// Namespace for HMC saved invites in PostgreSQL
const INVITES_NAMESPACE = 'hmc-saved-invites';

// Setting key
const SETTING_KEY = 'saved-invites';

/**
 * Generate a unique ID for invites
 */
const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
};

/**
 * Hook for managing saved invites
 */
export function useSavedInvites() {
  // Saved invites list
  const [savedInvites, setSavedInvites] = useState<SavedInvite[]>([]);

  // Current active invite (being edited)
  const [activeInviteId, setActiveInviteId] = useState<string | null>(null);

  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Load invites from backend on mount
  useEffect(() => {
    const loadInvites = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const invites = await getSetting<SavedInvite[]>(SETTING_KEY, {
          namespace: INVITES_NAMESPACE,
          defaultValue: [],
        });

        setSavedInvites(invites || []);
      } catch (err) {
        console.error('[useSavedInvites] Failed to load invites:', err);
        setError('Failed to load saved invites');
      } finally {
        setIsLoading(false);
      }
    };

    loadInvites();
  }, []);

  // Get active invite
  const activeInvite = useMemo(() => {
    if (!activeInviteId) return null;
    return savedInvites.find((inv) => inv.id === activeInviteId) || null;
  }, [activeInviteId, savedInvites]);

  // Get sorted invites (most recent first)
  const sortedInvites = useMemo(() => {
    return [...savedInvites].sort((a, b) =>
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  }, [savedInvites]);

  // Create a new invite
  const createInvite = useCallback(async (
    name: string,
    data: Omit<SavedInvite, 'id' | 'name' | 'createdAt' | 'updatedAt' | 'exportHistory' | 'isCompleted'>
  ): Promise<SavedInvite> => {
    setIsSaving(true);
    const now = new Date().toISOString();
    const newInvite: SavedInvite = {
      ...data,
      id: generateId(),
      name,
      createdAt: now,
      updatedAt: now,
      exportHistory: [],
      isCompleted: false,
    };

    const updated = [...savedInvites, newInvite];
    setSavedInvites(updated);
    setActiveInviteId(newInvite.id);

    try {
      await setSetting(SETTING_KEY, updated, {
        namespace: INVITES_NAMESPACE,
      });
      return newInvite;
    } catch (err) {
      console.error('[useSavedInvites] Failed to create invite:', err);
      // Rollback
      setSavedInvites(savedInvites);
      setActiveInviteId(null);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [savedInvites]);

  // Update an existing invite
  const updateInvite = useCallback(async (
    id: string,
    updates: Partial<Omit<SavedInvite, 'id' | 'createdAt'>>
  ): Promise<SavedInvite | null> => {
    const existingInvite = savedInvites.find((inv) => inv.id === id);
    if (!existingInvite) {
      console.warn('[useSavedInvites] Invite not found:', id);
      return null;
    }

    setIsSaving(true);
    const updatedInvite: SavedInvite = {
      ...existingInvite,
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    const updated = savedInvites.map((inv) =>
      inv.id === id ? updatedInvite : inv
    );
    setSavedInvites(updated);

    try {
      await setSetting(SETTING_KEY, updated, {
        namespace: INVITES_NAMESPACE,
      });
      return updatedInvite;
    } catch (err) {
      console.error('[useSavedInvites] Failed to update invite:', err);
      // Rollback
      setSavedInvites(savedInvites);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [savedInvites]);

  // Delete an invite
  const deleteInvite = useCallback(async (id: string): Promise<void> => {
    const updated = savedInvites.filter((inv) => inv.id !== id);
    setSavedInvites(updated);

    if (activeInviteId === id) {
      setActiveInviteId(null);
    }

    try {
      await setSetting(SETTING_KEY, updated, {
        namespace: INVITES_NAMESPACE,
      });
    } catch (err) {
      console.error('[useSavedInvites] Failed to delete invite:', err);
      // Rollback
      setSavedInvites(savedInvites);
      throw err;
    }
  }, [savedInvites, activeInviteId]);

  // Add export history entry
  const addExportEntry = useCallback(async (
    inviteId: string,
    exportType: 'html' | 'eml' | 'mail-merge',
    filename: string
  ): Promise<void> => {
    const existingInvite = savedInvites.find((inv) => inv.id === inviteId);
    if (!existingInvite) {
      console.warn('[useSavedInvites] Invite not found for export:', inviteId);
      return;
    }

    const newEntry: ExportHistoryEntry = {
      id: generateId(),
      type: exportType,
      filename,
      exportedAt: new Date().toISOString(),
    };

    await updateInvite(inviteId, {
      exportHistory: [...existingInvite.exportHistory, newEntry],
    });
  }, [savedInvites, updateInvite]);

  // Mark invite as completed
  const markAsCompleted = useCallback(async (
    inviteId: string,
    deliveredRecipients: InviteRecipient[],
    sentEmailHtml?: string,
    sentEmailSubject?: string
  ): Promise<void> => {
    await updateInvite(inviteId, {
      isCompleted: true,
      completedAt: new Date().toISOString(),
      deliveredRecipients,
      sentEmailHtml,
      sentEmailSubject,
    });
  }, [updateInvite]);

  // Undo mark as completed (revert to draft)
  const markAsIncomplete = useCallback(async (
    inviteId: string
  ): Promise<void> => {
    await updateInvite(inviteId, {
      isCompleted: false,
      completedAt: undefined,
      // Keep deliveredRecipients for historical reference
    });
  }, [updateInvite]);

  // Clear active invite (start fresh)
  const clearActiveInvite = useCallback(() => {
    setActiveInviteId(null);
  }, []);

  // Load an existing invite for editing
  const loadInvite = useCallback((id: string): SavedInvite | null => {
    const invite = savedInvites.find((inv) => inv.id === id);
    if (invite) {
      setActiveInviteId(id);
    }
    return invite || null;
  }, [savedInvites]);

  return {
    // State
    savedInvites: sortedInvites,
    activeInvite,
    activeInviteId,
    isLoading,
    isSaving,
    error,

    // Actions
    createInvite,
    updateInvite,
    deleteInvite,
    addExportEntry,
    markAsCompleted,
    markAsIncomplete,
    loadInvite,
    clearActiveInvite,
    setActiveInviteId,
  };
}

export default useSavedInvites;
